import React, { useEffect } from "react";
import { Box, Typography, Stack, Divider } from "@mui/material";
import { fillParent, scrollingBox, footerFlex } from "utils/base-styles";
import PageTitle from "components/layout/PageTitle";
import Footer from "components/layout/Footer";
import { useLocation } from "react-router-dom";
import {
  AvatarPlayground,
  CategorySection,
  DistributionListManagement,
  PulseCategoryPrefrence,
  ThemePreference,
} from "features/Settings";
import { Helmet } from "react-helmet-async";

const PreferencePage = () => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const id = queryParams.get("id");

  const scrollToSection = (targetId) => {
    if (!targetId) {
      return;
    }
    if (targetId === "top") {
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    const element = document.getElementById(targetId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  useEffect(() => {
    if (!id) {
      scrollToSection("top");
      return;
    }

    const timerId = window.setTimeout(() => scrollToSection(id), 350);

    return () => {
      window.clearTimeout(timerId);
    };
  }, [id]);

  return (
    <Box
      sx={{
        ...fillParent,
        ...scrollingBox,
        ...footerFlex,
      }}
    >
      <Helmet>
        <title>Sooth - Profile - Preferences</title>
      </Helmet>
      <Stack>
        <PageTitle sx={{ wordBreak: "break-word" }}>Preferences</PageTitle>
        <Typography>
          Welcome to the preferences page. Here, you will find all the options
          available to make life on Sooth easier. As your needs change, come
          back here and update things to better suit your research activities.
          As Sooth grows, we will expand the options available to make
          researching smoother for you.
        </Typography>
        <Divider sx={{ my: 4 }} />
        <Typography variant="h4">Color Scheme</Typography>
        <Typography>
          The color scheme allows you to set Sooth's interface to light or dark
          mode. You can also tell Sooth to match your system settings as well.
        </Typography>
        <ThemePreference />
        <Divider sx={{ my: 4 }} />
        <Typography id="change-avatar" variant="h4">
          Change Avatar
        </Typography>
        <Typography>
          By default your avatar is created using your user information as a
          seed. If you would like to customize how your avatar looks,
          please change the options below and then save it when you are happy
          with your new look!
        </Typography>
        <AvatarPlayground />
        <Divider sx={{ my: 3 }} />
        <Typography  id="distribution-lists"  variant="h4">Distribution Lists</Typography>
        <Typography>
          Creating a Distribution List allows you to conveniently share Collections 
          with groups of users with whom you may frequently want to share 
          (e.g. 3rd period Social Studies). Please make sure to use the same email that corresponds 
          to their Sooth account!
        </Typography>
        <Typography>
          When you’re ready to share a Collection with a Distribution List, just type the “@” symbol 
          on the Manage Access page of each Collection. Your Distribution Lists will appear in the drop-down menu.
        </Typography>
        <Typography>
          Removing individual users from a Distribution List is easy, just click the Edit button beside the list name, 
          then select “Remove”. If you have already shared a Collection with that user, 
          they will retain a copy of it once you remove them.
        </Typography>
        <DistributionListManagement />
        <Divider sx={{ my: 3 }} />
        <Typography variant="h4">Search Categories</Typography>
        <Typography>
          Sooth's search categories are customizable to make researching easier.
          Change the order to keep more important categories near the top. Hide
          categories that are not of interest to you. You can always access all
          the categories in the search box at the top of the page, so you can
          always take advantage of essential research sources.
        </Typography>
        <CategorySection />
        <Divider sx={{ my: 4 }} />

        <Typography id="pulse-category" variant="h4">
          Pulse Topics
        </Typography>
        <PulseCategoryPrefrence />
        <Divider sx={{ my: 3 }} />
      </Stack>
      <Footer sx={{ px: 0 }} />
    </Box>
  );
};

export default PreferencePage;

